import { theme } from "../../Themes/Themes";
import searchIcon from "../../Media/SearchBarIcon.svg";
import "./SearchBar.css";
import useStore from "../../Store/Store";
import "./SearchBar.css";
import { useEffect, useState, useMemo } from "react";
import Clear from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import Tooltip from "@mui/material/Tooltip";
import debounce from "lodash/debounce";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

export function handleSearchBarFocus(drawioEvt) {
  // select the search bar
  // const searchInput: HTMLInputElement = document.querySelector(".search-input");
  // if (searchInput) {
  //   searchInput.focus();
  // }
  useStore.getState().postToDrawio({
    drawioRequestId: drawioEvt.drawioRequestId,
    action: "SEARCH_BAR_FOCUS_RESPONSE",
    status: "SUCCESS",
  });
}

export const SearchBar: React.FC = () => {
  const {
    fuse,
    setSearchResults,
    setCurrentPath,
    currentRepo,
    setSelectedTab,
    searchResults,
    fileIndex,
    documentMap,
    indexingProgress,
    downloadProgress,
    session,
  } = useStore((state) => ({
    fuse: state.fuse,
    setSearchResults: state.setSearchResults,
    setCurrentPath: state.setCurrentPath,
    currentRepo: state.currentRepo,
    setSelectedTab: state.setSelectedTab,
    currentPath: state.currentPath,
    searchResults: state.searchResults,
    fileIndex: state.fileIndex,
    documentMap: state.documentMap,
    indexingProgress: state.indexingProgress,
    downloadProgress: state.downloadProgress,
    session: state.session,
  }));

  const [searchValue, setSearchValue] = useState("");
  const [exactMatch, setExactMatch] = useState(true);

  // Create a debounced version of renderSearchResults using useMemo
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (!value?.length) {
          setSearchResults(null);
        } else if (value.length < 2) {
          // Don't trigger search for just 1 character
          return;
        } else if (fileIndex || fuse) {
          setCurrentPath(currentRepo);
          if (!value) {
            setSearchResults(null);
            return;
          }
          let allResults: SearchResult[] = [];
          // Always perform filename/cell search with Fuse first
          if (fuse && !exactMatch) {
            try {
              const fuseResults = fuse.search(value, {
                exact: exactMatch,
                limit: 15,
              });
              const fuseSearchResults = fuseResults.map((result) => {
                // Calculate filename match score
                const fileName =
                  result.item.fileName || result.item.cellName || "";
                const fileNameLower = fileName.toLowerCase();
                const searchValueLower = value.toLowerCase();

                // Higher score for exact matches
                let score = 1;
                if (fileNameLower === searchValueLower) {
                  score = 2; // Exact match
                } else if (fileNameLower.startsWith(searchValueLower)) {
                  score = 1.5; // Starts with search term
                } else if (fileNameLower.includes(searchValueLower)) {
                  score = 1; // Contains search term
                }
                return {
                  path: result.item.path,
                  content: fileName,
                  score: score,
                  type: "file",
                  isExactMatch: fileNameLower === searchValueLower,
                };
              });
              // Sort file results by score
              allResults = fuseSearchResults.sort((a, b) => b.score - a.score);
            } catch (e) {
              console.error("Error performing file search:", e);
            }
          }
          // Perform code search using FlexSearch if available and indexing is complete
          if (
            (session.mode === "local" ||
              session.mode === "github" ||
              session.mode === "unauthenticatedGithub") &&
            fileIndex &&
            documentMap &&
            !indexingProgress.isIndexing &&
            !downloadProgress.isDownloading
          ) {
            try {
              // Search for matches in the indexed content
              const resultIds = fileIndex.search(value, {
                exact: exactMatch,
              });

              // Track processed IDs to avoid duplicates
              const processedIds = new Set();

              // Process each search result
              for (const id of resultIds) {
                try {
                  // Skip if we've already processed this ID
                  if (processedIds.has(id)) continue;
                  processedIds.add(id);

                  // Get the document from the map and validate it
                  const document = documentMap.get(id.toString());
                  if (!document || !document.content || !document.path)
                    continue;

                  // Get the content of the document
                  const contentStr = document.content;
                  const searchValueLower = value.toLowerCase();
                  const contentLower = contentStr.toLowerCase();

                  // Find all matches of the search term in the content
                  let matches = [];
                  let searchIndex = contentLower.indexOf(searchValueLower);

                  // Skip if we already have an exact filename match for this path
                  if (
                    allResults.some(
                      (r) => r.path === document.path && r.isExactMatch
                    )
                  ) {
                    continue;
                  }

                  // Find all occurrences and extract context (40 chars before and after match) for each match
                  while (searchIndex >= 0) {
                    const contextStart = Math.max(0, searchIndex - 40);
                    const contextEnd = Math.min(
                      contentStr.length,
                      searchIndex + value.length + 40
                    );
                    const extractedText = contentStr.substring(
                      contextStart,
                      contextEnd
                    );
                    const matchIndex = extractedText
                      .toLowerCase()
                      .indexOf(searchValueLower);

                    matches.push({
                      text: extractedText,
                      start: matchIndex,
                      end: matchIndex + value.length,
                      originalStart: searchIndex,
                      originalEnd: searchIndex + value.length,
                    });

                    // Look for next match
                    searchIndex = contentLower.indexOf(
                      searchValueLower,
                      searchIndex + value.length
                    );
                  }

                  // Add the search result with all extracted contexts and score
                  allResults.push({
                    path: document.path,
                    content: contentStr,
                    score: matches.length > 0 ? 0.8 : 0.5, // Higher score if we found direct matches
                    matches,
                    type: "content",
                  });
                } catch (e) {
                  console.error("Error processing search result:", e);
                }
              }
            } catch (e) {
              console.error("Error performing code search:", e);
            }
          }
          // Sort all results by score
          const sortedResults = allResults.sort((a, b) => {
            // Check if we have any exact filename matches
            const hasExactFileMatches = allResults.some(
              (result) => result.type === "file" && result.isExactMatch
            );

            // If there are no exact filename matches and both items are different types
            if (!hasExactFileMatches && a.type !== b.type) {
              // Prioritize content matches when there are no filename matches
              return a.type === "content" ? -1 : 1;
            }

            // Compare by score first
            const scoreDiff = b.score - a.score;
            if (scoreDiff !== 0) return scoreDiff;

            // If scores are equal and both are content type with matches
            if (
              a.type === "content" &&
              b.type === "content" &&
              a.matches &&
              b.matches
            ) {
              // Prioritize files with more matches
              const matchCountDiff = b.matches.length - a.matches.length;
              if (matchCountDiff !== 0) return matchCountDiff;
            }

            // Then by path length (shorter paths first)
            return a.path.length - b.path.length;
          });
          setSearchResults(sortedResults);
          setSelectedTab(0);
        }
      }, 500),
    [
      fileIndex,
      documentMap,
      fuse,
      setSearchResults,
      setSelectedTab,
      currentRepo,
      setCurrentPath,
      indexingProgress.isIndexing,
      downloadProgress.isDownloading,
      session.mode,
      exactMatch,
    ]
  );

  const searchHandler = (searchValue: string) => {
    setSearchValue(searchValue);
    debouncedSearch(searchValue);
  };

  // Cleanup the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  function handleClearSearch() {
    setSearchValue("");
    setSearchResults(null);
  }

  useEffect(() => {
    if (!searchResults) {
      setSearchValue("");
    }
  }, [searchResults]);

  useEffect(() => {
    if (searchValue) {
      debouncedSearch(searchValue);
    }
  }, [exactMatch]);

  // Helper function to get search placeholder text
  const getSearchPlaceholder = () => {
    if (downloadProgress.isDownloading) {
      switch (downloadProgress.phase) {
        case "downloading":
          const downloadPercent = downloadProgress.totalBytes
            ? Math.round(
                (downloadProgress.bytesDownloaded /
                  downloadProgress.totalBytes) *
                  100
              )
            : 0;
          return `Downloading repository (${downloadPercent}%)...`;
        case "extracting":
          return "Extracting repository archive...";
        case "processing":
          const processPercent = downloadProgress.totalFiles
            ? Math.round(
                (downloadProgress.filesProcessed /
                  downloadProgress.totalFiles) *
                  100
              )
            : 0;
          return `Processing files (${processPercent}%)...`;
        default:
          return "Downloading repository...";
      }
    }

    if (indexingProgress.isIndexing) {
      const indexPercent = Math.round(
        (indexingProgress.current / indexingProgress.total) * 100
      );
      return `Indexing code (${indexPercent}%)...`;
    }

    // Check if user is authenticated for better messaging
    const accessToken = localStorage.getItem("accessToken");
    const isAuthenticated =
      accessToken && accessToken !== "null" && accessToken !== "undefined";

    // Show appropriate placeholder based on session mode and indexing status
    if (
      session.mode === "local" ||
      ((session.mode === "github" ||
        session.mode === "unauthenticatedGithub") &&
        fileIndex &&
        documentMap &&
        documentMap.size > 0)
    ) {
      return "Search code and filenames...";
    }

    // Different messaging for authenticated vs unauthenticated users
    if (session.mode === "github" || session.mode === "unauthenticatedGithub") {
      if (isAuthenticated) {
        return "Search filenames (supports private repos)...";
      } else {
        return "Search filenames (public repos only)...";
      }
    }

    return "Search filenames...";
  };

  return (
    <div
      className="search-bar-wrapper"
      style={{ display: "flex", alignItems: "center", gap: "8px" }}
    >
      <div className="search-bar" style={{ position: "relative", flex: 1 }}>
        <img alt="Search Icon" className="search-icon" src={searchIcon} />
        <input
          className="search-input"
          placeholder={getSearchPlaceholder()}
          onChange={(e) => searchHandler(e.target.value)}
          value={searchValue}
          style={{
            fontSize: "14px",
            outline: "none",
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.custom.darkestGrey,
            paddingRight:
              session.mode !== "local" &&
              !(fileIndex && documentMap && documentMap.size > 0)
                ? "32px"
                : "8px",
          }}
          onFocus={() => {
            // If there are search results, ensure they're displayed when the search bar is focused
            if (searchResults && searchResults.length > 0) {
              setSelectedTab(0); // Switch to RepoTab to show search results
              setCurrentPath(currentRepo); // Reset path to repo root
            }
          }}
        />
        {session.mode !== "local" &&
          !(fileIndex && documentMap && documentMap.size > 0) && (
            <Tooltip
              title="Code-level search will be available after repository download completes"
              placement="bottom"
            >
              <HelpOutlineSharpIcon
                sx={{
                  fontSize: 20,
                  color: theme.custom.darkGrey,
                  cursor: "help",
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Tooltip>
          )}
      </div>
      <Tooltip
        title="Click to switch between fuzzy and exact matches"
        placement="bottom"
      >
        <FormControlLabel
          control={
            <Switch
              checked={exactMatch}
              onChange={(_, checked) => setExactMatch(checked)}
              color="primary"
              size="small"
            />
          }
          label={exactMatch ? "Exact Match" : "Fuzzy Match"}
          labelPlacement="end"
          sx={{
            marginLeft: 0,
            marginRight: 0,
            userSelect: "none",
            ".MuiFormControlLabel-label": {
              fontSize: 12,
              color: theme.custom.darkGrey,
            },
          }}
        />
      </Tooltip>
      {searchValue && (
        <IconButton
          edge="end"
          onClick={handleClearSearch}
          aria-label="clear search"
          size="small"
        >
          <Clear fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

export default SearchBar;
